// Historical backtest, MCO + ECO paired per year — Rogers screen 03. Reuses
// buildBacktestReport for MCO (payment_factor already served, never recomputed); pairs each
// year with eco_backtest_comparison's served eco_payment_factor scaled to this farmer the
// same way. Cost panel (expected_cost, harvest_cost) rides along from the same
// backtest_margins rows already piped in for MCO, joined by year — those are county-level,
// never farmer-scaled.

import { buildBacktestReport, groupBacktestRowsByYear } from "./mcoBacktest.js";
import { capHarvestPrice, roundHalfUp } from "./mcoModel.js";
import { num, numOrNull, toShareDecimal } from "./mcoShared.js";

// ECO's first crop year is 2021 (payment_spec.md / app_implementation_guide.md). Years
// before that get a null ECO entry, never a zero bar.
export const ECO_FIRST_YEAR = 2021;

const round2 = (v) => roundHalfUp(v, 2);

/**
 * ECO's $/ac indemnity for one backtest year, scaled to this farmer's own protection —
 * same shape as farmerIndemnityFromBacktest, sourced from eco_backtest_comparison's served
 * eco_payment_factor (never recomputed).
 *
 * ECO protection is built from the projected price ONLY (Rogers 2026-08-07): the
 * harvest-price reset is MCO's rule; ECO already counts a higher harvest price through
 * its payment factor's revenue comparison. Using max(pp, hp) here double-counted the
 * price rise — 2022 corn showed ECO protection $48.02/ac (harvest 6.86) instead of
 * ~$41.30 (projected 5.90). Regression rule: whenever hp > pp, ECO and MCO protection
 * must differ in proportion to the two prices.
 *
 * @param {number} year
 * @param {object|null} row — one eco_backtest_comparison row, or null if none served
 * @param {object} farmer — { approvedYield, protectionFactor, sharePct, acres }
 */
export function ecoIndemnityForBacktestYear(year, row, farmer = {}) {
  if (year < ECO_FIRST_YEAR) {
    return { applicable: false, ready: false, offered: null, blockers: [], note: null, result: null };
  }
  if (!row) {
    return {
      applicable: true,
      ready: false,
      offered: null,
      blockers: ["No ECO comparison data for this year"],
      note: null,
      result: null,
    };
  }

  const hasOffer = row.has_comparable_eco_offer !== false && Number(row.has_comparable_eco_offer) !== 0;
  const offeredInCounty = row.eco_offered_in_county !== false && Number(row.eco_offered_in_county) !== 0;
  const note = String(row.comparison_note ?? row.comparisonNote ?? "") || null;
  if (!hasOffer || !offeredInCounty) {
    return { applicable: true, ready: false, offered: false, blockers: [], note, result: null };
  }

  const pf = num(row.eco_payment_factor ?? row.ecoPaymentFactor);
  const pp = num(row.eco_projected_price ?? row.ecoProjectedPrice);
  const hpRaw = num(row.eco_harvest_price ?? row.ecoHarvestPrice);
  const cr = num(row.eco_coverage_range ?? row.ecoCoverageRange);
  const approvedYield = num(farmer.approvedYield);
  const protectionFactor = num(farmer.protectionFactor ?? 1);
  const share = toShareDecimal(farmer.sharePct);

  const blockers = [];
  if (!(pf >= 0) || Number.isNaN(pf)) blockers.push("eco_payment_factor missing");
  if (!(pp > 0)) blockers.push("eco_projected_price missing");
  if (!(cr > 0)) blockers.push("eco_coverage_range missing");
  if (!(approvedYield > 0)) blockers.push("approved yield (APH) missing");
  if (!(protectionFactor > 0)) blockers.push("protection factor missing");
  if (blockers.length) return { applicable: true, ready: false, offered: true, blockers, note, result: null };

  const hp = capHarvestPrice(hpRaw > 0 ? hpRaw : pp, pp);
  const protectionPerAcre = round2(approvedYield * pp * cr * protectionFactor * share);
  const indemnityPerAcre = round2(pf * protectionPerAcre);

  return {
    applicable: true,
    ready: true,
    offered: true,
    blockers: [],
    note,
    result: {
      paymentFactor: pf,
      protectionPerAcre,
      indemnityPerAcre,
      projectedPrice: pp,
      harvestPrice: hp,
      eay: num(row.eco_expected_area_yield ?? row.ecoExpectedAreaYield),
      fay: num(row.eco_final_area_yield ?? row.ecoFinalAreaYield),
      planCode: String(row.eco_insurance_plan_code ?? row.ecoInsurancePlanCode ?? ""),
      planVariant: String(row.plan_variant ?? row.planVariant ?? ""),
    },
  };
}

/**
 * Pair MCO (buildBacktestReport) and ECO (eco_backtest_comparison) per year, scaled to this
 * farmer. Never mixes endorsement vintages — inherits that guard from buildBacktestReport.
 *
 * @param {object[]} mcoRows — mirror BacktestMargins rows (single vintage)
 * @param {object[]} ecoRows — mirror EcoBacktestComparison rows
 * @param {object} farmer
 * @param {string} [practiceFilter] — irrigated | non_irrigated
 */
export function buildMcoEcoBacktestReport(mcoRows, ecoRows, farmer = {}, practiceFilter = "") {
  const mcoReport = buildBacktestReport(mcoRows, farmer, practiceFilter);
  if (!mcoReport.years.length) return { ...mcoReport, years: [] };

  const rawMcoByYear = groupBacktestRowsByYear(mcoRows, practiceFilter);
  const ecoByYear = groupBacktestRowsByYear(ecoRows, practiceFilter);

  const years = mcoReport.years.map((y) => {
    const rawMco = rawMcoByYear.get(y.year);
    const eco = ecoIndemnityForBacktestYear(y.year, ecoByYear.get(y.year) || null, farmer);
    return {
      ...y,
      expectedCost: numOrNull(rawMco?.expected_cost ?? rawMco?.expectedCost),
      harvestCost: numOrNull(rawMco?.harvest_cost ?? rawMco?.harvestCost),
      expectedRevenue: numOrNull(rawMco?.expected_revenue ?? rawMco?.expectedRevenue),
      harvestRevenue: numOrNull(rawMco?.harvest_revenue ?? rawMco?.harvestRevenue),
      eco,
    };
  });

  return { ...mcoReport, years };
}

// The three lines the year-detail card plots, in reading order: what came in, what went out,
// what was left. `riseIsGood` is the only per-line judgement — costs are the one line where up
// is the bad direction.
const DELTA_LINES = [
  { id: "revenue", label: "Revenue", expectedKey: "expectedRevenue", actualKey: "harvestRevenue", riseIsGood: true },
  { id: "cost", label: "Input costs", expectedKey: "expectedCost", actualKey: "harvestCost", riseIsGood: false },
  { id: "margin", label: "Margin", expectedKey: "expectedMargin", actualKey: "harvestMargin", riseIsGood: true },
];

/**
 * Expected -> actual movement for one backtest year, one entry per line above.
 *
 * Every value is county-level $/ac (never farmer-scaled), so margin = revenue - cost holds and
 * the three deltas reconcile: delta(margin) = delta(revenue) - delta(cost). That is why the card
 * draws all three bars against ONE dollar scale instead of each line's own percentage — three
 * independently normalized bars would show a margin move that doesn't square with the two moves
 * that produced it.
 *
 * `pct` rides along for the label but is null unless the expected base is positive: a zero or
 * negative expected margin has no meaningful percentage move.
 *
 * @param {object} year — one entry from buildMcoEcoBacktestReport().years
 * @returns {{id: string, label: string, expected: number|null, actual: number|null,
 *   delta: number|null, pct: number|null, riseIsGood: boolean}[]}
 */
export function backtestYearDeltas(year) {
  return DELTA_LINES.map(({ id, label, expectedKey, actualKey, riseIsGood }) => {
    const expected = numOrNull(year?.[expectedKey]);
    const actual = numOrNull(year?.[actualKey]);
    const delta = expected != null && actual != null ? round2(actual - expected) : null;
    return {
      id,
      label,
      expected,
      actual,
      delta,
      pct: delta != null && expected > 0 ? Math.round((delta / expected) * 1000) / 10 : null,
      riseIsGood,
    };
  });
}
